import {
    DateTimePicker,
    LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useState, useCallback } from "react";
import {
    GridActionsCellItem,
    useGridApiRef,
} from "@mui/x-data-grid-pro";
import { requiredCell } from "../../../js/utils/inputValidations";
import DataGridComponent from "../../Widgets/DataGridComponent";
import { useSelector, useDispatch } from "react-redux";
import LanguageIcon from "@mui/icons-material/LanguageOutlined";
import { disableKeyCellEdit, TranslationDialog } from "../../DataGrid/cst_DataGrid";
import { t } from "react-i18nify";
import { BasicTooltip } from "../../Widgets/StyledComponents";
import { updateHighlightedRows } from "../../../js/redux/actions/userPref";
import { useLocation } from "react-router-dom";

import {
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { VTextField } from "../../Widgets/Custom Inputs/CustomInputs";
import { BasicButton }  from "../../Widgets/Custom Inputs/Buttons";
import { FormProvider, useForm } from "react-hook-form";
import { useCheckAndRevalidateToken } from "../../../js/utils/customHooks";
import { SessionVerificationDialog } from "../../Layouts/CommonLayout";
import { DialogActionsForm } from "../../Widgets/CustomSurfaces";
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';

const NewFeatureMessage = ({ label="New Feature Message" , baseURL="NewFeaturesMessage", readOnly=false, specificColumns,paramURL="" }) => {
    const MetaCompanyManager  = useSelector(state => state.auth?.MetaCompanyManager);
    const CompanyManager  = useSelector(state => state.auth?.CompanyManager);
    const filterParamURL = new URLSearchParams(paramURL).toString(); // Will serialize object
    const dispatch = useDispatch();
    const location = useLocation();
    const currentLocation = location.pathname;
    const apiRef = useGridApiRef();
    specificColumns = specificColumns ? specificColumns(apiRef) : [];

    const locale = useSelector(state => state.i18n.locale);
    const [propsEnglishValue, setOpenEnglishValue] = useState(false);
    const beforeCommitRow = useCallback(
        row => {
            if (row.isNew && locale !== "ENG") {
                return new Promise((resolve, reject) => {
                    setOpenEnglishValue({
                        open: true,
                        setOpen: setOpenEnglishValue,
                        resolve,
                        reject,
                        row,
                        label,
                    });
                }).then(englishValue => ({
                    ...row,
                    EnglishValue: englishValue,
                }));
            }
            return row;
        },
        [setOpenEnglishValue, locale, label]
    );


    const cellFocusField = "VersionNumber";
    const gridSpecificColumns = [
        {
            field: "VersionNumber",
            headerName: t("field.Version"),
            width: 150,
            editable: true,
            preProcessEditCellProps: params => {
                const hasError = requiredCell(params, apiRef, t("field.Version"), "VersionNumber");
                return { ...params.props, error: hasError };
            },
            valueFormatter: (params) => params.value ?? '',
            renderEditCell: (params) => {
                return (
                    <TextField
                      value={params.value ??  ''}
                      defaultValue={params.value ?? ''}
                      style={{width:"100%"}}
                      variant="outlined"
                      onChange={(newValue) => {
                        params.api.setEditCellValue({
                          ...params,
                          value: newValue.target.value,
                        });
                      }}
                      renderInput={(props) => <TextField {...props} />}
                    />
                );
              },
        },
        {
            field: "Value",
            headerName: t("field.Text"),
            width: 745,
            flex: 1,
            editable: true,
            preProcessEditCellProps: params => {
                const hasError = requiredCell(params, apiRef, t("field.Text"), "Value");
                return { ...params.props, error: hasError };
            },
            renderCell: (params) => (
               <div style={{ whiteSpace: 'pre-line', wordWrap: 'break-word' }}>
                  {params.value ?? ''}
                </div>
              ),
            valueFormatter: params => params.value  ?? '',
            renderEditCell: (params) => {
                return (
                    <TextField
                      value={params.value ?? ''}
                      defaultValue={params.value ?? ''}
                      multiline rows={3}
                      style={{width:"100%",height:"92px"}}
                      variant="outlined"
                      onChange={(newValue) => {
                        params.api.setEditCellValue({
                          ...params,
                          value: newValue.target.value,
                        });
                      }}
                      renderInput={(props) => <TextField {...props} />}
                    />
                );
              },
        },
        {
            field: "CreatedOn",
            headerName: t("field.ReleaseDate"),
            width: 205,
            //flex: 1,
            editable: true,
            preProcessEditCellProps: params => {
                const hasError = requiredCell(params, apiRef, t("field.ReleaseDate"), "CreatedOn");
                return { ...params.props, error: hasError };
            },
            valueFormatter: params => params.value ? new Date(params.value).toLocaleString(navigator.language) : '',
            renderEditCell: (params) => {
                return (
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateTimePicker
                        viewRenderers={{
                            hours: renderTimeViewClock,
                            minutes: renderTimeViewClock,
                            seconds: renderTimeViewClock,
                          }}
                      value={params.value ? new Date(params.value) : null}
                      defaultValue={params.value ? new Date(params.value) : null}
                      style={{width:"100%"}}
                    //   ampm={false}
                      onChange={(newValue) => {
                        params.api.setEditCellValue({
                          ...params,
                          value: newValue,
                        });
                      }}
                      renderInput={(props) => <TextField {...props} />}
                    />
                  </LocalizationProvider>
                );
              },
        },
        ...specificColumns,
    ];

    const [open, setOpen] = useState(false);
    const [rowParams, setRowParams] = useState();
    const specificActionsColumns = (params, editing, isLocalLoading) => [
        <>
            {(MetaCompanyManager || CompanyManager) && (
                <GridActionsCellItem
                    label={t("common.Translate")}
                    icon={
                        <BasicTooltip title={t("common.Translate")}>
                            <LanguageIcon />
                        </BasicTooltip>
                    }
                    onClick={() => {
                        dispatch(
                            updateHighlightedRows({
                                CurrentLocation: currentLocation,
                                VisitedRowID: params.row.ID,
                            })
                        );
                        setOpen(true);
                        setRowParams(params.row);
                    }}
                    disabled={editing || isLocalLoading}
                />
            )}
        </>,
    ];

    return (
        <>
            <TranslationDialog
                label={label}
                open={open}
                setOpen={setOpen}
                rowParams={rowParams}
                baseURL={baseURL}
                maxChar={1000}
            />

            <DataGridComponent
                apiRef={apiRef}
                isRowHeightAuto={true}
                hideActionColumn={readOnly===true ? true : (MetaCompanyManager===true || CompanyManager===true) && readOnly===false ? false : true}
                isAddDisabled={readOnly===true ? true : (MetaCompanyManager===true || CompanyManager===true) && readOnly===false ? false : true}
                baseURL={baseURL}
                cellFocusField={cellFocusField}
                label={label}
                gridSpecificColumns={gridSpecificColumns}
                specificActionsColumns={specificActionsColumns}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "CreatedOn", sort: "desc" }],
                    },
                }}
                isCellEditable={(params) => disableKeyCellEdit(params)}
                beforeCommitRow={beforeCommitRow}
                paramURL={filterParamURL}
            />
            <EnglishValueDialog {...propsEnglishValue} />
        </>
    );
};

export default NewFeatureMessage;

export const EnglishValueDialog = ({
    open,
    setOpen,
    resolve,
    reject,
    row,
    label,
}) => {
    const methods = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const { reset, handleSubmit, formState } = methods;

    ////////////////////////////////// TOKEN REVALIDATION \\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\
    const [sessionVerification, setSessionVerification] = useState(false);
    useCheckAndRevalidateToken(setSessionVerification, [
        formState.isSubmitting,
    ]);
    ////////////////////////////////// TOKEN REVALIDATION \\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\\

    const manageSubmit = async data => {
        setOpen(false);
        reset();
        resolve(data.EnglishValue);
    };
    const manageCancel = useCallback(() => {
        setOpen(false);
        reset();
        reject();
    }, [reject, setOpen, reset]);

    return (
        <Dialog open={Boolean(open)} sx={{ w: 1 }} fullWidth>
            {sessionVerification ? (
                <SessionVerificationDialog
                    sessionVerification={sessionVerification}
                />
            ) : null}
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(manageSubmit)}>
                    <DialogTitle sx={{ typography: "body1", display: "flex" }}>
                        {t("common.AddingEnglish")}{" "}
                        <Typography
                            color="secondary"
                            sx={{ ml: 1 / 2, textTransform: "lowercase" }}
                        >
                            {label}
                        </Typography>{" "}
                        : {row?.Value}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <VTextField
                                    label={`${t("field.Value")}: ${t(
                                        "languages.English"
                                    )}`}
                                    fieldName={"EnglishValue"}
                                    validation={{
                                        required: {
                                            value: true,
                                            message: t(
                                                "input.validation.required"
                                            ),
                                        },
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActionsForm>
                        <BasicButton
                            type="button"
                            style={{
                                position: "relative",
                                margin: 4,
                            }}
                            onClick={manageCancel}
                        >
                            {t("common.Cancel")}
                        </BasicButton>
                        <BasicButton
                            type="submit"
                            style={{ margin: 4, marginRight: 0 }}
                        >
                            {t("common.Save")}
                        </BasicButton>
                    </DialogActionsForm>
                </form>
            </FormProvider>
        </Dialog>
    );
};
